import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from './Api';
import StarRating from './StarRating';
import API_BASE_URL from "./config";

const AI_KEYS = ['chatgpt', 'claude', 'gemini'];

const AI_META = {
  chatgpt: { titel: 'ChatGPT', cssClass: 'chatgpt-review', textField: 'chat_gpt_text', updateField: 'chat_gpt_update' },
  claude:  { titel: 'Claude',  cssClass: 'claude-review',  textField: 'claude_text',   updateField: 'claude_update' },
  gemini:  { titel: 'Gemini',  cssClass: 'gemini-review',  textField: 'gemini_text',   updateField: 'gemini_update' },
};

const renderLines = (text) =>
  text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((line) => line.trim())
    .map((line, index) => {
      if (line.includes(':')) {
        const colonIndex = line.indexOf(':');
        const key = line.substring(0, colonIndex);
        const value = line.substring(colonIndex + 1).trim();
        return (
          <p key={index}>
            <strong>{key}:</strong> {value}
          </p>
        );
      }
      return <p key={index}>{line}</p>;
    });

const WeinDetails = ({ isLoggedIn }) => {
  const { id } = useParams();
  const [wein, setWein] = useState(null);
  const [bewertungen, setBewertungen] = useState([]);
  const [neueBewertung, setNeueBewertung] = useState(0);
  const [kommentar, setKommentar] = useState("");
  const [getrunkenDaten, setGetrunkenDaten] = useState([]);
  const [gekauftDaten, setGekauftDaten] = useState([]);
  const [verschenktDaten, setVerschenktDaten] = useState([]);
  const [regalPositionen, setRegalPositionen] = useState([]);
  const [kiste, setKiste] = useState(null);
  const [ausgewähltePosition, setAusgewähltePosition] = useState(null);
  const [ausgewählteRegalPosition, setAusgewählteRegalPosition] = useState([]);

  // Per-KI Start-Timer: startedAt=null → Text war beim ersten Load schon da.
  //                    startedAt=<ms>  → Text fehlte, Timer läuft bis Text nachkommt.
  const [kiStatus, setKiStatus] = useState({
    chatgpt: { startedAt: null },
    claude:  { startedAt: null },
    gemini:  { startedAt: null },
  });
  const kiStatusInitialized = useRef(false);
  const [now, setNow] = useState(Date.now());

  const navigate = useNavigate();

  const handleWeinNochmalKaufen = () => {
    if (wein) {
      navigate('/einkauf', {
        state: {
          winzer: wein.winzer,
          name: wein.name,
          rebsorte: wein.rebsorte,
          jahr: wein.jahr,
          preis: wein.preis,
          land: wein.land,
          region: wein.region,
          bezugsquelle: wein.bezugsquelle,
        },
      });
    }
  };

  const loadData = () => {
    if (!id) return;
    axios.get(`${API_BASE_URL}/wein/${id}`)
      .then((response) => {
        setWein(response.data.wein);
        setBewertungen(response.data.bewertungen);

        // Beim ersten Response: Timer für fehlende KI-Bewertungen starten.
        // Bei späteren Polls NICHT mehr neu setzen, sonst reset bei jedem Tick.
        if (!kiStatusInitialized.current) {
          const startedAt = Date.now();
          const initial = {};
          AI_KEYS.forEach((ki) => {
            initial[ki] = { startedAt: response.data.wein?.[AI_META[ki].textField] ? null : startedAt };
          });
          setKiStatus(initial);
          kiStatusInitialized.current = true;
        }
      })
      .catch((error) => {
        console.error('Fehler beim Laden der Wein-Details!', error);
      });

    axios.get(`${API_BASE_URL}/getrunken/${id}`)
      .then((response) => setGetrunkenDaten(response.data))
      .catch((error) => console.error('Fehler beim Laden der Getrunken-Daten!', error));

    axios.get(`${API_BASE_URL}/gekauft/${id}`)
      .then((response) => setGekauftDaten(response.data))
      .catch((error) => console.error('Fehler beim Laden der Gekauft-Daten!', error));

    axios.get(`${API_BASE_URL}/verschenkt/${id}`)
      .then((response) => setVerschenktDaten(response.data))
      .catch((error) => console.error('Fehler beim Laden der Verschenkt-Daten!', error));

    axios.get(`${API_BASE_URL}/regal/${id}`)
      .then((response) => setRegalPositionen(response.data))
      .catch((error) => console.error('Fehler beim Laden der Regalpositionen!', error));

    axios.get(`${API_BASE_URL}/kiste/${id}`)
      .then((response) => setKiste(response.data))
      .catch((error) => console.error('Fehler beim Laden der Kistenposition!', error));
  };

  useEffect(loadData, [id]);

  // Polling: solange mind. eine KI-Bewertung fehlt, jede Sekunde /wein/:id nachfragen.
  // Deps: nur die 3 Text-Felder — bei jedem Füllen re-runs der Effect, berechnet
  // `missing` neu und stoppt das Interval, sobald alle 3 da sind.
  const chatGptText = wein?.chat_gpt_text;
  const claudeText  = wein?.claude_text;
  const geminiText  = wein?.gemini_text;
  useEffect(() => {
    const missing = [!chatGptText, !claudeText, !geminiText].filter(Boolean).length;
    if (!id || missing === 0) return undefined;

    const intervalId = setInterval(() => {
      axios.get(`${API_BASE_URL}/wein/${id}`)
        .then((res) => {
          setWein(res.data.wein);
          setBewertungen(res.data.bewertungen);
        })
        .catch(() => { /* Netzfehler schweigend überspringen */ });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [id, chatGptText, claudeText, geminiText]);

  // Live-Ticker (500 ms) für hochzählende Sekunden-Anzeige in KI-Panels.
  useEffect(() => {
    const anyLoading = AI_KEYS.some(
      (ki) => kiStatus[ki].startedAt && !wein?.[AI_META[ki].textField]
    );
    if (!anyLoading) return undefined;
    const intervalId = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(intervalId);
  }, [kiStatus, chatGptText, claudeText, geminiText, wein]);

  const handleBewerten = () => {
    if (!id) return;
    api.post(`/auth/wein/${id}/bewerten`, {
      bewertung: neueBewertung,
      kommentar: kommentar,
    })
      .then(() => {
        console.log('Bewertung erfolgreich hinzugefügt!');
        setKommentar("");
        setNeueBewertung(0);
        return axios.get(`${API_BASE_URL}/wein/${id}`);
      })
      .then((response) => {
        setBewertungen(response.data.bewertungen);
        setWein(response.data.wein);
      })
      .catch((error) => console.error('Fehler beim Hinzufügen der Bewertung!', error));
  };

  const handleGetrunken = () => {
    if (!id) return;
    if (!ausgewähltePosition) {
      console.error("Es wurde keine Position ausgewählt");
      return;
    }

    handleBewerten();
    const isKiste = (ausgewähltePosition === "kiste");

    api.post(`/auth/getrunken/${id}`, {
      bewertung: neueBewertung,
      jahr: wein?.jahr,
      preis: wein?.preis,
      anzahlAusKiste: isKiste,
      regalPosition: (!isKiste && ausgewählteRegalPosition)
        ? { breite: ausgewählteRegalPosition.breite, hoehe: ausgewählteRegalPosition.hoehe }
        : undefined,
    })
      .then(() => {
        console.log('Wein erfolgreich als getrunken gespeichert');
        loadData();
      })
      .catch((error) => console.error('Fehler beim Speichern in der Getrunken-Tabelle!', error));
  };

  const handleVerschenkt = () => {
    if (!id) return;
    if (!ausgewähltePosition) {
      console.error("Es wurde keine Position ausgewählt");
      return;
    }

    const isKiste = (ausgewähltePosition === "kiste");

    api.post(`/auth/verschenkt/${id}`, {
      kommentar: kommentar,
      jahr: wein?.jahr,
      preis: wein?.preis,
      anzahlAusKiste: isKiste,
      regalPosition: (!isKiste && ausgewählteRegalPosition)
        ? { breite: ausgewählteRegalPosition.breite, hoehe: ausgewählteRegalPosition.hoehe }
        : undefined,
    })
      .then(() => {
        console.log('Wein erfolgreich als verschenkt gespeichert');
        setKommentar("");
        loadData();
      })
      .catch((error) => console.error('Fehler beim Speichern in der Verschenkt-Tabelle!', error));
  };

  const isButtonDisabledBewertung = (kommentar.trim() === "" || neueBewertung <= 0);
  const isButtonDisabledGetrunken = (isButtonDisabledBewertung || ausgewähltePosition == null);
  const isButtonDisabledVerschenkt = (ausgewähltePosition == null);

  const KiBewertungPanel = ({ kiKey }) => {
    const { titel, cssClass, textField, updateField } = AI_META[kiKey];
    const text = wein?.[textField];
    const updateDate = wein?.[updateField];
    const startedAt = kiStatus[kiKey]?.startedAt;
    const elapsed = startedAt ? ((now - startedAt) / 1000).toFixed(1) : null;

    return (
      <div className={`ai-review-section ${cssClass}`}>
        <h3>{titel} Sommelier-Bewertung</h3>
        {text ? (
          <>
            <p className="ai-review-timestamp">
              Aktualisiert am: {new Date(updateDate).toLocaleDateString()}
            </p>
            <div className="ai-review-content">{renderLines(text)}</div>
          </>
        ) : startedAt ? (
          <p className="text-muted mb-0">
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            Bewertung wird erstellt… {elapsed}s
          </p>
        ) : (
          <p className="text-muted">Noch nicht verfügbar</p>
        )}
      </div>
    );
  };

  return (
    <div>
      <h2>Wein Details</h2>

      {/* Bildanzeige */}
      {wein && (wein.bild_pfad_full || wein.bild_pfad) && (
        <div>
          <img
            src={`${process.env.REACT_APP_API_URL.replace('/api', '')}/${wein.bild_pfad_full || wein.bild_pfad}`}
            alt={wein.name}
            style={{ maxWidth: '400px', width: '80%', height: 'auto' }}
          />
        </div>
      )}

      {/* Wein-Stammdaten */}
      {wein && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <strong style={{ width: '120px' }}>Winzer:</strong>
            <a
              href={`https://www.vivino.com/search/wines?q=${encodeURIComponent(`${wein.name} ${wein.winzer}`).replace(/%20/g, '+')}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {wein.winzer}
            </a>
          </div>
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <strong style={{ width: '120px' }}>Name:</strong>
            <span>{wein.name}</span>
          </div>
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <strong style={{ width: '120px' }}>Rebsorte:</strong>
            <span>{wein.rebsorte}</span>
          </div>
          {wein.farbe && wein.farbe !== '-' && (
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <strong style={{ width: '120px' }}>Farbe:</strong>
              <span>{wein.farbe}</span>
            </div>
          )}
          {wein.stil && wein.stil !== '-' && (
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <strong style={{ width: '120px' }}>Stil:</strong>
              <span>{wein.stil}</span>
            </div>
          )}
          {wein.praedikat && wein.praedikat !== '-' && (
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <strong style={{ width: '120px' }}>Prädikat:</strong>
              <span>{wein.praedikat}</span>
            </div>
          )}
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <strong style={{ width: '120px' }}>Jahr:</strong>
            <span>{wein.jahr}</span>
          </div>
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <strong style={{ width: '120px' }}>Preis:</strong>
            <span>{typeof wein.preis === 'number' ? `${wein.preis.toFixed(2)} €` : '-'}</span>
          </div>
          {wein.land && wein.land !== '-' && (
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <strong style={{ width: '120px' }}>Land:</strong>
              <span>{wein.land}</span>
            </div>
          )}
          {wein.region && wein.region !== '-' && (
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <strong style={{ width: '120px' }}>Region:</strong>
              <span>{wein.region}</span>
            </div>
          )}
          {wein.bezugsquelle && wein.bezugsquelle !== '-' && (
            <div style={{ display: 'flex', marginBottom: '10px' }}>
              <strong style={{ width: '120px' }}>Bezugsquelle:</strong>
              <span>{wein.bezugsquelle}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <strong style={{ width: '120px' }}>Bewertung:</strong>
            <StarRating rating={wein.bewertung} setRating={() => {}} readonly={true} />
          </div>
        </div>
      )}

      {/* Bewertung abgeben */}
      {isLoggedIn && wein && (
        <div style={{ marginTop: '40px' }}>
          <h3>Bewertung abgeben</h3>
          <StarRating rating={neueBewertung} setRating={setNeueBewertung} />
          <textarea
            className="form-control"
            rows="3"
            value={kommentar}
            onChange={(e) => setKommentar(e.target.value)}
            placeholder="Kommentar eingeben..."
            style={{ marginBottom: '10px' }}
          />

          {(regalPositionen.length > 0 || kiste) && (
            <div style={{ marginTop: '20px' }}>
              <h4>Wähle die Position</h4>
              {regalPositionen.map((pos, index) => {
                const positionId = pos.id !== undefined && pos.id !== null ? String(pos.id) : `pos-${index}`;
                return (
                  <div key={`regal-${positionId}`}>
                    <input
                      type="radio"
                      name="actposition"
                      value={positionId}
                      checked={ausgewähltePosition === positionId}
                      onChange={(e) => {
                        setAusgewähltePosition(e.target.value);
                        setAusgewählteRegalPosition({ id: pos.id, breite: pos.breite, hoehe: pos.hoehe });
                      }}
                    />
                    <label>Reihe {pos.hoehe + 1}, Spalte {pos.breite + 1}</label>
                  </div>
                );
              })}
              {kiste && (
                <div key="kiste">
                  <input
                    type="radio"
                    name="actposition"
                    value="kiste"
                    checked={ausgewähltePosition === 'kiste'}
                    onChange={(e) => {
                      setAusgewähltePosition(e.target.value);
                      setAusgewählteRegalPosition(null);
                    }}
                  />
                  <label>Außerhalb Regal (in Kiste)</label>
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            {(regalPositionen.length > 0 || kiste) && (
              <button
                onClick={handleVerschenkt}
                className="btn btn-warning"
                disabled={isButtonDisabledVerschenkt}
              >
                Verschenkt
              </button>
            )}
            <button
              onClick={handleBewerten}
              className="btn btn-primary"
              disabled={isButtonDisabledBewertung}
            >
              Bewerten
            </button>
            {(regalPositionen.length > 0 || kiste) && (
              <button
                onClick={handleGetrunken}
                className="btn btn-danger"
                disabled={isButtonDisabledGetrunken}
              >
                Getrunken
              </button>
            )}
          </div>

        </div>
      )}

      {/* KI-Bewertungen — drei Panels, jeweils mit eigenem Zustand */}
      <div style={{ marginTop: '40px' }}>
        {AI_KEYS.map((ki) => <KiBewertungPanel key={ki} kiKey={ki} />)}
      </div>

      {/* Bewertungen */}
      <div style={{ marginTop: '40px' }}>
        {bewertungen.length > 0 && <h3>Alle Bewertungen</h3>}
        {bewertungen.map((bewertung, index) => (
          <div key={bewertung.ID || index} style={{ borderBottom: '1px solid #ccc', padding: '10px 0' }}>
            <div><strong>Datum:</strong> {new Date(bewertung.CreatedAt).toLocaleString()}</div>
            <div><strong>Sterne:</strong>{" "}<StarRating rating={bewertung.bewertung} setRating={() => {}} readonly={true} /></div>
            <div><strong>Kommentar:</strong> {bewertung.kommentar}</div>
          </div>
        ))}
      </div>

      {/* Getrunken */}
      <div style={{ marginTop: '40px' }}>
        {getrunkenDaten.length > 0 && <h3>Getrunken</h3>}
        {getrunkenDaten.length > 0 && (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Datum</th><th>Jahr</th><th>Preis (€)</th><th>Bewertung</th>
              </tr>
            </thead>
            <tbody>
              {getrunkenDaten.map((item, index) => (
                <tr key={`getrunken-${item.ID || index}`}>
                  <td>{item.created_at.split('T')[0]}</td>
                  <td>{item.jahr}</td>
                  <td>€{item.preis.toFixed(2)}</td>
                  <td>{item.bewertung}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Einkäufe */}
      <div style={{ marginTop: '40px' }}>
        {isLoggedIn && wein && (
          <>
            <h3>Einkäufe</h3>
            <div style={{ marginBottom: '20px' }}>
              <button onClick={handleWeinNochmalKaufen} className="btn btn-primary">
                Diesen Wein noch mal kaufen
              </button>
            </div>
          </>
        )}
        {gekauftDaten.length > 0 && (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Datum</th><th>Jahr</th><th>Anzahl</th><th>Preis (€)</th><th>Gesamt (€)</th><th>Bezugsquelle</th>
              </tr>
            </thead>
            <tbody>
              {gekauftDaten.map((item, index) => {
                const anzahl = item.anzahl || 1;
                return (
                  <tr key={`gekauft-${item.ID || index}`}>
                    <td>{item.created_at.split('T')[0]}</td>
                    <td>{item.jahr}</td>
                    <td>{anzahl}</td>
                    <td>€{item.preis.toFixed(2)}</td>
                    <td>€{(item.preis * anzahl).toFixed(2)}</td>
                    <td>{item.bezugsquelle || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Verschenkt */}
      <div style={{ marginTop: '40px' }}>
        {verschenktDaten.length > 0 && <h3>Verschenkt</h3>}
        {verschenktDaten.length > 0 && (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Datum</th><th>Jahr</th><th>Preis (€)</th><th>Kommentar</th>
              </tr>
            </thead>
            <tbody>
              {verschenktDaten.map((item, index) => (
                <tr key={`verschenkt-${item.ID || index}`}>
                  <td>{item.created_at.split('T')[0]}</td>
                  <td>{item.jahr}</td>
                  <td>€{item.preis.toFixed(2)}</td>
                  <td>{item.kommentar || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default WeinDetails;
